import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { EventLogEntry, EventType } from './dtos';
import { gameLogger } from './logger';
import ControllerLocal from './tmcontrollerlocal';
import Turfmaster, { StateMachineValue } from './tmmodel';
import { USERID_FOR_SERVER_MESSAGES } from './utilConstants';

// Required for buildEventLogEntry() timestamp. Before the split, the server created the timestamp
// in saveEventLogEntry() (models/game.ts). The client path has no DB call and must build full
// EventLogEntry objects here, so we set a UTC timestamp so training and server events stay consistent.
dayjs.extend(utc);

/**
 * Intermediate base class that adds event log generation capabilities to ControllerLocal.
 *
 * This class provides:
 * - Category A method overrides that capture pre-mutation state for undo deltas
 * - Event log entry creation logic with payload enrichment and undo delta support
 *
 * Subclasses (ControllerLocalProxy, ClientControllerLocal in client repo) handle persistence:
 * - ControllerLocalProxy: saves to MongoDB
 * - ClientControllerLocal: pushes to this.eventLog array
 */
export class ControllerLocalWithEventLog extends ControllerLocal {
  /**
   * Ephemeral undo-delta for the current event handler invocation.
   *
   * ## Lifecycle
   *
   * _eventPrev is a short-lived message-passing mechanism between proxy method
   * overrides and the proxy's sendMsg(). It is NOT persistent server state.
   *
   * 1. A proxy method override (e.g. selectMoveEvent) captures the specific
   *    game fields that are about to be mutated, and stores them here.
   * 2. The override calls super.handler(), which mutates state and then
   *    calls this.sendMsg() internally.
   * 3. The proxy's sendMsg() reads _eventPrev, attaches it as payload.prev,
   *    saves the event to the database, and clears _eventPrev = null.
   *
   * Steps 1-3 happen within a single await chain. _eventPrev is never non-null
   * between separate event handler invocations.
   */
  protected _eventPrev: Record<string, unknown> | null = null;

  /**
   * Pre-calcTurndices turndices snapshot for the SELECTDICES event.
   * Same lifecycle and safety guarantees as _eventPrev. Separate field
   * because startTurn() emits two events (STARTTURN then SELECTDICES);
   * _eventPrev would be consumed by the first sendMsg call.
   */
  protected _selectDicesPrev: Record<string, unknown> | null = null;

  /** SELECTMOVEOPP: capture horse position, cards, dropped/finished, turnpos, turnorder before super.selectMoveEvent() mutates them. */
  public async selectMoveEvent(params: any) {
    const game = this.getGame();
    const horse = game?.horses?.[params.horseid];
    if (horse) {
      this._eventPrev = {
        horseIndex: params.horseid,
        horseField: horse.field
          ? { pos: horse.field.pos, prog: horse.field.prog, x: horse.field.x, y: horse.field.y }
          : null,
        horseDropped: horse.dropped,
        horseFinished: horse.finished,
        horseCards: horse.cards.map((c: number[]) => [...c]),
        turnpos: game.turnpos,
        turnorder: [...game.turnorder],
      };
    }
    return super.selectMoveEvent(params);
  }

  /** SELECTDICESOPP: capture turndicescur before super.selectDicesEvent() assigns it. */
  public async selectDicesEvent(dicesid: any, userid: string) {
    const game = this.getGame();
    if (game) this._eventPrev = { turndicescur: game.turndicescur };
    return super.selectDicesEvent(dicesid, userid);
  }

  /** STARTROUND: capture all horses' dropped/finished before super.startRound() resets them. */
  public async startRound() {
    const game = this.getGame();
    if (game) {
      this._eventPrev = {
        horsesDropped: game.horses.map((h: { dropped: boolean }) => h.dropped),
        horsesFinished: game.horses.map((h: { finished: boolean }) => h.finished),
      };
    }
    return super.startRound();
  }

  /** MERGECARDS: capture horse cards and turnpos before super.mergeCardsEvent() mutates them. */
  public async mergeCardsEvent(params: any) {
    const game = this.getGame();
    const horse = game?.horses?.[params.horseid];
    if (horse) {
      this._eventPrev = {
        horseIndex: params.horseid,
        horseCards: horse.cards.map((c: number[]) => [...c]),
        turnpos: game.turnpos,
      };
    }
    return super.mergeCardsEvent(params);
  }

  /** ADDBONUSCARDS: capture horse bonuscards and cards before super.addBonusCardsEvent() mutates them. */
  public async addBonusCardsEvent(params: any) {
    const game = this.getGame();
    const horse = game?.horses?.[params.horseid];
    if (horse) {
      this._eventPrev = {
        horseIndex: params.horseid,
        horseBonuscards: horse.bonuscards,
        horseCards: horse.cards.map((c: number[]) => [...c]),
      };
    }
    return super.addBonusCardsEvent(params);
  }

  /** STARTTURN: capture turndices before super.startTurn() calls calcTurndices() and sends SELECTDICES. Uses _selectDicesPrev. */
  public async startTurn() {
    const game = this.getGame();
    if (game && Array.isArray(game.turndices))
      this._selectDicesPrev = { turndices: [...game.turndices] };
    return super.startTurn();
  }

  /**
   * Commit the event log entry. Override in subclasses to persist (e.g. DB); default is in-memory only.
   * Called once per event from sendMsg(); ownership of the write is explicit (no double-write).
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async commitEventLogEntry(entry: EventLogEntry, _params: any): Promise<EventLogEntry> {
    this.eventLog.push(entry);
    return entry;
  }

  /**
   * Override sendMsg to build once, commit once via hook, then dispatch.
   * Event-log ownership is explicit: only commitEventLogEntry() writes the entry.
   */
  public async sendMsg(state: StateMachineValue, params: any): Promise<false | void> {
    try {
      const entry = this.buildEventLogEntry(state, params);
      if (entry) {
        const committedEntry = await this.commitEventLogEntry(entry, params);
        if (params && typeof params === 'object') params.newEventLogEntry = committedEntry;
      }
    } catch (error) {
      gameLogger.error(
        { err: error, state },
        'error building event log entry, continuing without it',
      );
    }

    return super.sendMsg(state, params);
  }

  /**
   * Build an event log entry for the given state and params.
   *
   * This method handles:
   * - Filtering out transient states (SELECTMOVE, SELECTCARDDICE)
   * - Creating EventLogEntry with senderId, timestamp, eventType
   * - Sanitizing payload (removing view, eventLog properties)
   * - Enriching payload per event type
   * - Adding Category B undo deltas (ENDTURN, ENDROUND, ENDGAME, SELECTDICES)
   * - Consuming Category A undo deltas from _eventPrev and _selectDicesPrev
   *
   * @param state - State machine state
   * @param params - Message parameters
   * @returns EventLogEntry or null if state is transient
   */
  protected buildEventLogEntry(state: StateMachineValue, params: any): EventLogEntry | null {
    // create eventLog entry for every message
    // except for SELECTMOVE and SELECTCARDDICE because these are transient states for checking possible moves within the client
    if (
      state === Turfmaster.stateMachine.SELECTMOVE ||
      state === Turfmaster.stateMachine.SELECTCARDDICE
    ) {
      return null;
    }

    const stateName = Turfmaster.getStateName(state);
    const eventType = (
      stateName in EventType ? EventType[stateName as keyof typeof EventType] : EventType.UNKNOWN
    ) as EventType;
    // determine senderId: use params.userid if available, otherwise "SERVER" for system messages
    const senderId = params?.userid || USERID_FOR_SERVER_MESSAGES;

    // sanitize payload: remove properties that contain circular references or non-serializable objects
    const payload = params ? { ...params } : {};
    // remove 'view' property which contains circular references (view -> controller -> views)
    delete payload.view;
    // remove 'eventLog' property to prevent excessive logging
    delete payload.eventLog;

    /**
     * Serialize Move object and its nested Field objects to prevent Redux non-serializable value errors.
     *
     * Rationale:
     * - Redux requires all state to be serializable (plain objects, arrays, primitives)
     * - The event log is stored in Redux state (`overlay.eventLog`) for replay/history functionality
     * - Move and Field are class instances, not plain objects, which causes Redux warnings/errors
     * - We use targeted serialization (only serialize move.fields) rather than deep serialization
     *   because:
     *   a) Payload structure is simple: { userid, move, carddice }
     *   b) Only move.fields contains class instances (Field objects)
     *   c) Typical move.fields array size: 1-10 items (very small)
     *   d) More performant: O(n) where n = number of fields vs traversing entire payload tree
     *   e) Explicit: Clear about what we're fixing
     *   f) No dependencies: Pure JavaScript, no lodash needed
     *
     * Performance: O(n) where n = number of fields (typically 1-10). Very fast.
     *
     * Note: This code runs in both contexts:
     * - Client-side (ClientControllerLocal): Required for Redux serialization
     * - Server-side (ControllerLocalProxy): Harmless - MongoDB stores JSON/BSON anyway, so class
     *   instances would be serialized during save. Explicit serialization ensures consistency
     *   between server and client event logs and is negligible performance-wise (runs only on
     *   user actions, not high-frequency operations).
     */
    if (
      payload.move &&
      typeof payload.move === 'object' &&
      'fields' in payload.move &&
      'drop' in payload.move
    ) {
      payload.move = {
        fields: Array.isArray(payload.move.fields)
          ? payload.move.fields.map((field: any) => {
              // Convert Field class instance to plain object by extracting enumerable properties
              if (field && typeof field === 'object') {
                const plainField: Record<string, any> = {};
                for (const key in field) {
                  if (Object.prototype.hasOwnProperty.call(field, key)) {
                    plainField[key] = field[key];
                  }
                }
                return plainField;
              }
              return field;
            })
          : [],
        drop: payload.move.drop ?? false,
      };
    }

    // Enrich payload for deterministic replay reducers (forward/backward)
    const game = this.getGame();

    // NEWMESSAGE: if caller provided senderName, it is persisted as-is.
    if (game) {
      switch (eventType) {
        case EventType.STARTGAME:
          payload.turndiceplayerid = game.turndiceplayerid;
          if (Array.isArray(game.turnorder)) payload.turnorder = [...game.turnorder];
          break;
        case EventType.MERGECARDS:
          if (typeof this.getHorseId() === 'number') payload.horseid = this.getHorseId();
          if (Array.isArray(this.lastRemovedCards))
            payload.removedCards = [...this.lastRemovedCards];
          break;
        case EventType.SELECTMOVEOPP:
          if (typeof this.getHorseId() === 'number') payload.horseid = this.getHorseId();
          // Move object serialization is handled above in the general sanitization step
          break;
        case EventType.SELECTDICES:
          if (Array.isArray(game.turndices)) payload.turndices = [...game.turndices];
          break;
        case EventType.SELECTDICESOPP:
          if (Array.isArray(game.turndices)) payload.turndices = [...game.turndices];
          if (typeof game.turndicescur === 'number') payload.turndicescur = game.turndicescur;
          break;
        case EventType.ENDTURN:
          // Backward reducer needs previous turntype/turndiceplayerid/turnpos. Controller should pass
          // previousTurntype, previousTurndiceplayerid, previousTurnpos in params when emitting ENDTURN.
          break;
        default:
          break;
      }

      // ── Per-event undo deltas for in-game history backward navigation ──
      // Category B events: state changes AFTER sendMsg, so current game fields are the "before" values.
      // Category A events: prev was captured by proxy method override in this._eventPrev.
      if (eventType === EventType.ENDTURN) {
        payload.prev = {
          turntype: game.turntype,
          turndiceplayerid: game.turndiceplayerid,
          turnpos: game.turnpos,
          turnorder: [...game.turnorder],
        };
      } else if (eventType === EventType.ENDROUND) {
        payload.prev = {
          round: game.round,
          turntype: game.turntype,
          turndiceplayerid: game.turndiceplayerid,
          turnpos: game.turnpos,
          turnorder: [...game.turnorder],
          horseFields: game.horses.map(
            (h: { field: { pos: number; prog: number; x: number; y: number } | null }) =>
              h.field ? { pos: h.field.pos, prog: h.field.prog, x: h.field.x, y: h.field.y } : null,
          ),
          horsesRanks: game.horses.map((h: { rank: number; totalrank: number }) => ({
            rank: h.rank,
            totalrank: h.totalrank,
          })),
          horsesHandi: game.horses.map((h: { handi: number }) => h.handi),
          horsesPoints: game.horses.map((h: { points: number[] }) => [...h.points]),
          horsesFinished: game.horses.map((h: { finished: boolean }) => h.finished),
          horsesDropped: game.horses.map((h: { dropped: boolean }) => h.dropped),
        };
      } else if (eventType === EventType.ENDGAME) {
        payload.prev = { currentState: game.currentState };
      } else if (eventType === EventType.SELECTDICES && this._selectDicesPrev) {
        payload.prev = this._selectDicesPrev;
        this._selectDicesPrev = null;
      }

      // Attach proxy-captured prev for Category A (pre-sendMsg) events
      if (this._eventPrev) {
        payload.prev = { ...(payload.prev || {}), ...this._eventPrev };
        this._eventPrev = null;
      }
    }

    // Create and return the event log entry (subclasses handle persistence).
    // UTC timestamp: same time base for client (training) and server events; see dayjs.extend(utc) above.
    const entry: EventLogEntry = {
      senderId,
      timestamp: dayjs().utc().toISOString(),
      eventType,
      payload,
    };

    return entry;
  }
}

export default ControllerLocalWithEventLog;
